import { Box, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import styles from "../ContractSearch.styles";
import {
  fill,
  matchingPassagesHeading,
  passageTag,
} from "../ContractSearchHelper";
import { MatchedContract, MatchedPassage } from "../ContractSearchTypes";
import { ChevronDownGlyph } from "./ContractSearchIcons";

interface MatchedContractsListProps {
  /** Prefix for element ids, unique per AI turn. */
  idPrefix: string;
  contracts: MatchedContract[];
  /** Indexed documents in the repository — the "of N contracts" denominator. */
  totalIndexed: number;
  expanded: Record<string, boolean>;
  onToggle: (documentId: string) => void;
  activePassageKey: string | null;
  onOpenPassage: (passage: MatchedPassage, contract: MatchedContract) => void;
}

/**
 * "Matched contracts": one expandable row per document the search touched, with
 * its quoted passages underneath. Rows and passages are real buttons with
 * `aria-expanded` / `aria-controls`, so the whole block works from the keyboard.
 */
const MatchedContractsList = ({
  idPrefix,
  contracts,
  totalIndexed,
  expanded,
  onToggle,
  activePassageKey,
  onOpenPassage,
}: MatchedContractsListProps) => {
  const copy = strings.contractSearch;
  if (!contracts.length) return null;

  return (
    <Box component="section" sx={styles.matchedCard} aria-label="Matched contracts">
      <Box sx={styles.matchedHeading}>
        <Typography component="h3" sx={styles.matchedHeadingLabel}>
          {copy.matchedHeading}
        </Typography>
        <Typography component="span" sx={styles.matchedHeadingCount}>
          {fill(copy.matchedCount, {
            matched: contracts.length,
            total: Math.max(totalIndexed, contracts.length),
          })}
        </Typography>
      </Box>

      {contracts.map((contract) => {
        const isOpen = !!expanded[contract.documentId];
        const panelId = `${idPrefix}-passages-${contract.documentId}`;
        const hasPassages = contract.passages.length > 0;
        return (
          <Box key={contract.documentId}>
            <Box
              component="button"
              type="button"
              sx={styles.contractRow}
              onClick={() => onToggle(contract.documentId)}
              aria-expanded={hasPassages ? isOpen : undefined}
              aria-controls={hasPassages ? panelId : undefined}
              disabled={!hasPassages}
            >
              <Box sx={styles.pdfTile} aria-hidden="true">
                PDF
              </Box>
              <Box sx={styles.contractInfo}>
                <Typography component="span" sx={styles.contractFile} title={contract.fileName}>
                  {contract.fileName}
                </Typography>
                {contract.meta && (
                  <Typography component="span" sx={styles.contractMeta} display="block">
                    {contract.meta}
                  </Typography>
                )}
              </Box>
              <Box component="span" sx={styles.contractTag(contract.used)}>
                {passageTag(contract)}
              </Box>
              {hasPassages && <ChevronDownGlyph sx={styles.chevron(isOpen)} />}
            </Box>

            {hasPassages && isOpen && (
              <Box id={panelId} sx={styles.passagesWrap}>
                <Typography component="h4" sx={styles.passagesHeading}>
                  {matchingPassagesHeading(contract.passages.length)}
                </Typography>
                <Box component="ul" sx={styles.passagesList}>
                  {contract.passages.map((passage) => {
                    const active = activePassageKey === passage.key;
                    return (
                      <Box component="li" key={passage.key}>
                        <Box
                          component="button"
                          type="button"
                          data-passage-key={passage.key}
                          sx={styles.passage(active)}
                          onClick={() => onOpenPassage(passage, contract)}
                          aria-pressed={active}
                          aria-label={`${copy.openInViewer}: ${passage.label}`}
                        >
                          <Box sx={styles.passageHead}>
                            {passage.page != null && (
                              <Typography component="span" sx={styles.passagePage}>
                                p. {passage.page}
                              </Typography>
                            )}
                            {passage.page != null && passage.linesLabel && (
                              <Box sx={styles.passageDot} aria-hidden="true" />
                            )}
                            {passage.linesLabel && (
                              <Typography component="span" sx={styles.passageLines}>
                                {passage.linesLabel}
                              </Typography>
                            )}
                            <Typography component="span" sx={styles.passageOpen} aria-hidden="true">
                              {copy.openInViewer}
                            </Typography>
                          </Box>
                          <Typography component="span" sx={styles.passageQuote} display="block">
                            {passage.quote}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default MatchedContractsList;
